import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { useSnackbar } from 'notistack'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import FormControl from '@material-ui/core/FormControl'
import FormHelperText from '@material-ui/core/FormHelperText'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import PgaMobileDatePicker from 'components/pga-time-picker/pga-mobile-date-picker'
import PgaMobileTimePicker from 'components/pga-time-picker/pga-mobile-time-picker'
import { SET_COACH_AVAILABILITY_OVERRIDE } from './queries'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'

const INVALID_INTERVAL_ERROR_MESSAGE = 'End time cannot occur before start time'

const CustomAvailabilityForm = ({ facilities, onSuccess, onCancel }) => {
  const { enqueueSnackbar } = useSnackbar()
  const inputWidth = '166px'

  const [selectedFacilityId, setSelectedFacilityId] = useState(
    facilities.length === 1 ? facilities[0].id : '',
  )
  const [date, setDate] = useState(null)
  const [startTime, setStartTime] = useState(null)
  const [endTime, setEndTime] = useState(null)

  const [setCoachAvailabilityOverride, { loading: isSubmitting }] = useMutation(
    SET_COACH_AVAILABILITY_OVERRIDE,
    {
      onCompleted: data => {
        const result = data.setCoachAvailabilityOverride
        if (result.success) {
          enqueueSnackbar('Custom availability saved', { variant: 'success' })
          onSuccess && onSuccess()
        } else {
          enqueueSnackbar(result.message || 'Failed to save', {
            variant: 'error',
          })
        }
      },
      onError: () => {
        enqueueSnackbar('Failed to save. Please try again.', {
          variant: 'error',
        })
      },
    },
  )

  const isInvalidInterval =
    startTime !== null && endTime !== null && endTime <= startTime

  const onSubmit = () => {
    setCoachAvailabilityOverride({
      variables: {
        coachFacilityId: selectedFacilityId,
        input: {
          date: date.toISODate(),
          timeSlots: [
            {
              startTime: startTime.toFormat('HH:mm:00'),
              endTime: endTime.toFormat('HH:mm:00'),
            },
          ],
        },
      },
    })
  }

  const canSubmit =
    selectedFacilityId &&
    date !== null &&
    startTime !== null &&
    endTime !== null &&
    !isInvalidInterval &&
    !isSubmitting

  return (
    <LocalizationProvider dateAdapter={CustomLuxonAdapter} locale="en-us">
      <Box mb={3}>
        {facilities.length === 1 ? (
          <FormControl variant="outlined" fullWidth>
            <TextField disabled value={facilities[0].name} variant="outlined" />
          </FormControl>
        ) : (
          <FormControl variant="outlined" fullWidth>
            <TextField
              select
              value={selectedFacilityId}
              variant="outlined"
              label="Select Facility"
              onChange={e => setSelectedFacilityId(e.target.value)}
            >
              {facilities.map(f => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        )}
      </Box>

      <Box mb={3}>
        <PgaMobileDatePicker
          label="Date"
          value={date}
          format="MMM dd, yyyy"
          onAccept={val => setDate(val)}
          disablePast
        />
      </Box>

      <Box display="flex" mb={2}>
        <Box mr={2} flexBasis={inputWidth}>
          <PgaMobileTimePicker
            id="from"
            label="From"
            value={startTime}
            validationErrors={isInvalidInterval}
            onAccept={val => setStartTime(val)}
          />
        </Box>
        <Box flexBasis={inputWidth}>
          <PgaMobileTimePicker
            id="to"
            label="To"
            value={endTime}
            validationErrors={isInvalidInterval}
            onAccept={val => setEndTime(val)}
          />
        </Box>
      </Box>

      {isInvalidInterval && (
        <Box mb={2}>
          <FormHelperText error>
            {INVALID_INTERVAL_ERROR_MESSAGE}
          </FormHelperText>
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="flex-start"
        flexDirection="row-reverse"
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          Save
        </Button>
        {onCancel && (
          <Button
            color="primary"
            size="large"
            onClick={onCancel}
            style={{ marginRight: '16px' }}
          >
            Cancel
          </Button>
        )}
      </Box>
    </LocalizationProvider>
  )
}

export default CustomAvailabilityForm
